import { Assets, Spritesheet, Texture } from 'pixi.js';
import { WebPDetector } from './WebPDetector';

/**
 * Configuration for atlas preload
 */
export interface PreloadConfig {
    preloadAtlases: string[];
    showProgress?: boolean;
    preferWebP?: boolean;
    disableBlobLoading?: boolean; // Disable blob-based loading
}

/**
 * Asset loading manager
 */
export class AssetLoader {
    private static instance: AssetLoader;
    private loadedAtlases: Set<string> = new Set();
    private loadingAtlases: Map<string, Promise<Spritesheet>> = new Map();
    private config: PreloadConfig;
    private basePath: string = 'assets';
    private webpSupported: boolean = false;
    private formatExtension: 'webp' | 'png' = 'png';

    private constructor(config: PreloadConfig) {
        this.config = {
            ...config,
            preferWebP: config.preferWebP !== false,
        };
    }

    /**
     * Initialize AssetLoader
     */
    public static async init(config: PreloadConfig): Promise<AssetLoader> {
        if (!AssetLoader.instance) {
            AssetLoader.instance = new AssetLoader(config);
        }

        await AssetLoader.instance.detectFormat();

        return AssetLoader.instance;
    }

    /**
     * Get AssetLoader instance
     */
    public static getInstance(): AssetLoader {
        if (!AssetLoader.instance) {
            throw new Error('AssetLoader is not initialized. Call AssetLoader.init() first.');
        }
        return AssetLoader.instance;
    }

    /**
     * Detect WebP support and select format
     */
    private async detectFormat(): Promise<void> {
        if (this.config.preferWebP) {
            this.webpSupported = await WebPDetector.isSupported();
            this.formatExtension = this.webpSupported ? 'webp' : 'png';
            
            if (this.config.showProgress) {
                console.log(`📦 Atlas format: ${this.formatExtension.toUpperCase()}`);
            }
        } else {
            this.formatExtension = 'png';
            if (this.config.showProgress) {
                console.log('📦 Atlas format: PNG (WebP disabled)');
            }
        }
    }

    /**
     * Load all atlases from preload list
     */
    public async preloadAssets(): Promise<void> {
        console.log('🎨 Starting asset preload...');
        
        // First detect WebP support
        await this.detectFormat();
        
        const startTime = Date.now();
        const atlasesToLoad = this.config.preloadAtlases;
        
        if (atlasesToLoad.length === 0) {
            console.log('⚠️  No atlases to preload');
            return;
        }

        try {
            // Load all atlases sequentially
            if (this.config.showProgress) {
                console.log(`📦 Loading ${atlasesToLoad.length} atlases...`);
            }

            for (let i = 0; i < atlasesToLoad.length; i++) {
                const atlasName = atlasesToLoad[i];
                
                // Load through unified method
                await this.performAtlasLoad(this.basePath, atlasName);
                this.loadedAtlases.add(atlasName);
                
                if (this.config.showProgress) {
                    const progress = Math.round(((i + 1) / atlasesToLoad.length) * 100);
                    console.log(`⏳ Loading progress: ${progress}%`);
                }
            }

            const loadTime = Date.now() - startTime;
            console.log(`✅ Preload completed in ${loadTime}ms`);
            console.log(`📦 Loaded atlases: ${atlasesToLoad.length}`);
            
        } catch (error) {
            console.error('❌ Error during asset preload:', error);
            throw error;
        }
    }

    /**
     * Load specific atlas
     */
    public async loadAtlas(path: string, atlasName: string): Promise<Spritesheet | null> {
        // Check format if not checked yet
        if (this.formatExtension === 'png' && this.config.preferWebP && !this.webpSupported) {
            await this.detectFormat();
        }
        
        // Form full path for uniqueness
        const fullAtlasPath = `${path}/${atlasName}`;
        
        // If already loaded, return from cache
        if (this.loadedAtlases.has(fullAtlasPath)) {
            return Assets.get(fullAtlasPath) as Spritesheet;
        }

        // If loading is in progress
        if (this.loadingAtlases.has(fullAtlasPath)) {
            await this.loadingAtlases.get(fullAtlasPath);
            return Assets.get(fullAtlasPath) as Spritesheet;
        }

        // Start loading
        const loadPromise = this.performAtlasLoad(path, atlasName);
        this.loadingAtlases.set(fullAtlasPath, loadPromise);

        try {
            const spritesheet = await loadPromise;
            this.loadedAtlases.add(fullAtlasPath);
            return spritesheet;
        } finally {
            this.loadingAtlases.delete(fullAtlasPath);
        }
    }

    /**
     * Remove atlas from internal tracking (used during unloading)
     */
    public removeAtlasFromTracking(path: string, atlasName: string): void {
        const fullAtlasPath = `${path}/${atlasName}`;
        this.loadedAtlases.delete(fullAtlasPath);
    }

    /**
     * Perform atlas loading
     */
    private async performAtlasLoad(path: string, atlasName: string): Promise<Spritesheet> {
        console.log(`📥 Loading atlas: ${atlasName}`);
        
        try {
            // Load JSON manually and modify image paths
            const jsonPath = `${window.location.origin}/${this.basePath}/${path}/${atlasName}.json`;
            const response = await fetch(jsonPath);
            const atlasData = await response.json();

            const imageExtension = this.webpSupported ? 'webp' : 'png';
            const imagePath = `${this.basePath}/${path}/${atlasName}.${imageExtension}`;
            const fullImagePath = `${window.location.origin}/${imagePath}`;

            let baseTextureSource: Texture;

            // If blob loading is disabled, load directly via HTMLImageElement
            if (this.config.disableBlobLoading) {
                baseTextureSource = await this.loadTextureDirectly(fullImagePath);
            } else {
                // Standard PixiJS Assets.load (may use blob)
                baseTextureSource = await Assets.load<Texture>(fullImagePath);
            }

            const baseTexture = baseTextureSource.baseTexture ?? (baseTextureSource as any).baseTexture;

            if (!baseTexture) {
                throw new Error(`Failed to load atlas texture "${atlasName}" (${imagePath})`);
            }

            const spritesheet = new Spritesheet(baseTexture, atlasData);
            await spritesheet.parse();

            // Form full atlas path
            const fullAtlasPath = `${path}/${atlasName}`;
            
            // Cache atlas inside PIXI Asset Cache using full path
            Assets.cache.set(fullAtlasPath, spritesheet);
            
            const format = this.webpSupported ? 'WebP' : 'PNG';
            const loadMethod = this.config.disableBlobLoading ? 'direct' : 'blob';
            console.log(`✅ Atlas loaded: ${atlasName} (${format}, ${loadMethod})`);
            
            return spritesheet;
        } catch (error) {
            console.error(`❌ Atlas loading error ${atlasName}:`, error);
            throw error;
        }
    }

    /**
     * Load texture directly without blob (bypasses chunked loading)
     */
    private async loadTextureDirectly(url: string): Promise<Texture> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = () => {
                try {
                    const texture = Texture.from(img);
                    resolve(texture);
                } catch (error) {
                    reject(error);
                }
            };
            
            img.onerror = () => {
                reject(new Error(`Failed to load image: ${url}`));
            };
            
            img.src = url;
        });
    }

    /**
     * Load multiple atlases
     */
    public async loadAtlases(path: string, atlasNames: string[]): Promise<void> {
        await Promise.all(atlasNames.map(name => this.loadAtlas(path, name)));
    }

    /**
     * Check if atlas is loaded
     */
    public isAtlasLoaded(atlasName: string): boolean {
        return this.loadedAtlases.has(atlasName);
    }

    /**
     * Get list of loaded atlases
     */
    public getLoadedAtlases(): string[] {
        return Array.from(this.loadedAtlases);
    }

    /**
     * Unload atlas from memory
     */
    public async unloadAtlas(atlasName: string): Promise<void> {
        if (!this.loadedAtlases.has(atlasName)) {
            return;
        }

        try {
            await Assets.unload(atlasName);
            this.loadedAtlases.delete(atlasName);
            console.log(`🗑️  Atlas unloaded: ${atlasName}`);
        } catch (error) {
            console.error(`❌ Atlas unloading error ${atlasName}:`, error);
            throw error;
        }
    }

    /**
     * Unload multiple atlases
     */
    public async unloadAtlases(atlasNames: string[]): Promise<void> {
        await Promise.all(atlasNames.map(name => this.unloadAtlas(name)));
    }

    /**
     * Get loading statistics
     */
    public getStats(): { loaded: number; loading: number; total: number } {
        return {
            loaded: this.loadedAtlases.size,
            loading: this.loadingAtlases.size,
            total: this.config.preloadAtlases.length,
        };
    }

    /**
     * Check if WebP is supported
     */
    public isWebPSupported(): boolean {
        return this.webpSupported;
    }

    /**
     * Get used atlas format
     */
    public getFormat(): 'webp' | 'png' {
        return this.formatExtension;
    }
}
AssetLoader.init({
    preloadAtlases: [],
    showProgress: false,
    preferWebP: true,
    disableBlobLoading: true, // Set to true to disable chunked/blob loading
});